import { z } from "https://esm.sh/zod@3.23.8";

const ContactForm = {
  svgSuccess:
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path></svg>',

  svgError:
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M236.8,188.09,149.35,36.22a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM120,104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm8,88a12,12,0,1,1,12-12A12,12,0,0,1,128,192Z"></path></svg>',

  parseI18n(form) {
    try {
      return JSON.parse(form.getAttribute("data-i18n") || "{}");
    } catch (_e) {
      return {};
    }
  },

  buildSchema(i18n) {
    return z.object({
      departamento: z.string().min(1, i18n.department || "Selecione o departamento."),
      nome: z.string().min(1, i18n.name || "Informe o nome e sobrenome."),
      email: z
        .string()
        .min(1, i18n.email || "Informe o e-mail.")
        .email(i18n.email_invalid || "E-mail inválido."),
      empresa: z.string().min(1, i18n.company || "Informe a empresa."),
      cnpj_cpf: z.string().min(1, i18n.cnpj_cpf || "Informe o CNPJ/CPF."),
      telefone: z.string().min(1, i18n.phone || "Informe o telefone."),
      estado: z.string().min(1, i18n.state || "Selecione o estado."),
      mensagem: z.string().min(1, i18n.message || "Informe a mensagem."),
      marketing_opt_in: z.boolean(),
      termos: z.literal("yes", {
        errorMap: function () {
          return { message: i18n.terms || "Você precisa aceitar os Termos de Uso." };
        },
      }),
    });
  },

  initAll() {
    document.querySelectorAll("[data-contact-form]").forEach((root) => {
      this.init(root);
    });
  },

  init(root) {
    const form = root.querySelector("[data-contact-form-element]");
    if (!form || form.dataset.contactInit === "1") return;
    form.dataset.contactInit = "1";

    const endpoint = form.getAttribute("data-contact-endpoint") || "";
    if (!endpoint) return;

    const i18n = this.parseI18n(form);
    const instance = {
      root,
      form,
      endpoint,
      i18n,
      schema: this.buildSchema(i18n),
      msgSuccess: form.getAttribute("data-msg-success") || "",
      msgError: form.getAttribute("data-msg-error") || "",
      statusEl: root.querySelector("[data-contact-status]"),
      submitBtn: root.querySelector("[data-contact-submit]"),
    };

    this.attachListeners(instance);
    this.enableSubmitButton(instance);
  },

  parseFormData(form) {
    const fd = new FormData(form);
    return {
      departamento: String(fd.get("departamento") ?? ""),
      nome: String(fd.get("nome") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      empresa: String(fd.get("empresa") ?? "").trim(),
      cnpj_cpf: String(fd.get("cnpj_cpf") ?? "").trim(),
      telefone: String(fd.get("telefone") ?? "").trim(),
      estado: String(fd.get("estado") ?? ""),
      mensagem: String(fd.get("mensagem") ?? "").trim(),
      marketing_opt_in: fd.get("marketing_opt_in") === "yes",
      termos: fd.get("termos") === "yes" ? "yes" : "",
    };
  },

  validate(instance) {
    return instance.schema.safeParse(this.parseFormData(instance.form));
  },

  clearFieldErrors(instance) {
    instance.form
      .querySelectorAll(".budget-form__field-error")
      .forEach(function (el) {
        el.textContent = "";
        el.hidden = true;
      });
  },

  clearErrorForField(instance, name) {
    if (!name) return;
    const el = instance.form.querySelector(
      '.budget-form__field-error[data-contact-field="' + name + '"]',
    );
    if (el) {
      el.textContent = "";
      el.hidden = true;
    }
  },

  showZodFieldErrors(instance, zodError) {
    const fe = zodError.flatten().fieldErrors;
    Object.keys(fe).forEach(function (key) {
      const messages = fe[key];
      if (!messages || !messages.length) return;
      const errEl = instance.form.querySelector(
        '.budget-form__field-error[data-contact-field="' + key + '"]',
      );
      if (errEl) {
        errEl.textContent = messages[0];
        errEl.hidden = false;
      }
    });
  },

  focusFirstInvalidField(instance, zodError) {
    const issue = zodError.issues[0];
    if (!issue || !issue.path || !issue.path.length) return;
    const key = String(issue.path[0]);
    const control =
      instance.form.querySelector('[name="' + key + '"]') ||
      (key === "termos" ? instance.form.querySelector('[name="termos"]') : null);
    if (control && typeof control.focus === "function") {
      control.focus();
    }
  },

  buildPayload(instance) {
    const data = new FormData(instance.form);
    data.append("submitted_at", new Date().toISOString());
    return data;
  },

  showStatus(instance, type) {
    if (!instance.statusEl) return;
    const text = type === "success" ? instance.msgSuccess : instance.msgError;
    instance.statusEl.innerHTML = "";
    instance.statusEl.hidden = false;

    const wrapper = document.createElement("div");
    wrapper.className =
      type === "success"
        ? "form-message alert alert--success h6 blocks-radius"
        : "form-error alert alert--error blocks-radius";
    wrapper.setAttribute("role", type === "success" ? "status" : "alert");
    wrapper.setAttribute("tabindex", "-1");

    const icon = document.createElement("span");
    icon.innerHTML = type === "success" ? this.svgSuccess : this.svgError;
    wrapper.appendChild(icon);
    wrapper.appendChild(document.createTextNode(" " + text));
    instance.statusEl.appendChild(wrapper);
    wrapper.focus();
    instance.statusEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
  },

  async postContactForm(instance, payload) {
    const res = await fetch(instance.endpoint, {
      method: "POST",
      body: payload,
    });

    let body = {};
    try {
      body = await res.json();
    } catch {
      body = {};
    }

    if (!res.ok) {
      throw new Error(body.message || "Erro " + res.status);
    }

    return body;
  },

  setLoading(instance, isLoading) {
    if (!instance.submitBtn) return;
    const btnText = instance.submitBtn.querySelector(".btn__text");
    const spinner = instance.submitBtn.querySelector(".budget-form__spinner");
    if (btnText) btnText.hidden = isLoading;
    if (spinner) spinner.hidden = !isLoading;
    instance.submitBtn.disabled = isLoading;
  },

  enableSubmitButton(instance) {
    if (instance.submitBtn) instance.submitBtn.disabled = false;
  },

  onFieldInput(instance, e) {
    const t = e.target;
    if (t && t.name) this.clearErrorForField(instance, t.name);
  },

  onFieldChange(instance, e) {
    const t = e.target;
    if (t && t.name) this.clearErrorForField(instance, t.name);
  },

  async onSubmit(instance, e) {
    e.preventDefault();
    this.clearFieldErrors(instance);

    const result = this.validate(instance);
    if (!result.success) {
      this.showZodFieldErrors(instance, result.error);
      this.focusFirstInvalidField(instance, result.error);
      this.enableSubmitButton(instance);
      return;
    }

    this.setLoading(instance, true);
    if (instance.statusEl) instance.statusEl.hidden = true;

    try {
      await this.postContactForm(instance, this.buildPayload(instance));
      this.showStatus(instance, "success");
      instance.form.reset();
      this.clearFieldErrors(instance);
    } catch {
      this.showStatus(instance, "error");
    } finally {
      this.setLoading(instance, false);
    }
  },

  attachListeners(instance) {
    instance.form.addEventListener("input", (e) => this.onFieldInput(instance, e));
    instance.form.addEventListener("change", (e) => this.onFieldChange(instance, e));
    instance.form.addEventListener("submit", (e) => this.onSubmit(instance, e));
  },
};

ContactForm.initAll();

document.addEventListener("shopify:section:load", (evt) => {
  evt.target.querySelectorAll("[data-contact-form]").forEach((root) => {
    ContactForm.init(root);
  });
});

export { ContactForm };
